import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  SarrafiyeCalc,
  TrLinkMatrix,
  FaqAccordion,
  buildTrSchema,
  trDateParts,
  goldRatesQueryOptions,
  useGramTry,
  formatTry,
} from "@/components/tr/shell";

const SLUG = "/tr/canli-gram-altin-fiyatlari";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "Bugün gram altın ne kadar ve anlık altın fiyatları nasıl takip edilir?", a: "Gram altın fiyatı ons altın (XAU/USD) değerinin USD/TRY kuru ile çarpılıp 31.1035 grama bölünmesiyle hesaplanır. Sayfamızdaki canlı tablo her 15 dakikada bir güncellenir ve anlık serbest piyasa satış değerini Türk Lirası olarak gösterir." },
  { q: "22 ayar gram altın fiyatı bugün canlı olarak ne kadar oldu?", a: "22 ayar gram altın, 24 ayar gram altının 22/24 oranına düşürülmüş halidir (yaklaşık %91.6 saflık). Yukarıdaki tabloda hem 24 ayar hem 22 ayar gram satış değeri anlık olarak listelenir." },
  { q: "400 gram altın kaç TL yapar ve anlık nasıl hesaplanır?", a: "400 gram altın değeri, gram altın satış fiyatının 400 ile çarpımıdır. Sayfadaki hesap makinesinden 'Gram Altın 24 Ayar' seçip adet olarak 400 girin — sonuç anlık TL cinsinden görüntülenir." },
];

export const Route = createFileRoute("/tr/canli-gram-altin-fiyatlari")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = trDateParts();
    const title = `Canlı Altın Fiyatları Bugün: Anlık Gram, Çeyrek ve Cumhuriyet Altını Kuru`;
    const description = `Anlık altın fiyatları ve canlı altın kuru takibi. Serbest piyasa, Kapalıçarşı, Harem Altın ve güncel gram altın verileri anında TL olarak burada.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "tr-TR" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "tr_TR" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "tr-TR", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildTrSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: GramAltinPage,
});

function GramAltinPage() {
  const dp = trDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Türkiye · Canlı Piyasa</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          Canlı Altın Fiyatları Bugün: Anlık Gram, Çeyrek ve Cumhuriyet Altını Kuru ({dp.dayNum} {dp.month} {dp.year}, {dp.day})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          Serbest piyasada gram altın alış-satış makası, çeyrek altın kuru ve Cumhuriyet Altını değeri anlık olarak Türk Lirası bazında güncellenir. Aşağıdaki tablo Kapalıçarşı fiyat trendini takip eder.
        </p>
      </header>
      <LiveChart title={`Gram Altın Grafiği — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <MillionGramBreakdown />
      <SarrafiyeCalc />
      <FaqAccordion items={FAQS} />
      <TrLinkMatrix current={SLUG} />
    </main>
  );
}

function MillionGramBreakdown() {
  const { gram24, gram22 } = useGramTry();
  const rows = [1, 5, 10, 50, 100, 250, 500, 1000];
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">Gram Altın Ağırlık Tablosu — Anlık TL Değerleri</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">Ağırlık</th>
              <th className="px-3 py-2 text-right">24 Ayar Satış</th>
              <th className="px-3 py-2 text-right">22 Ayar Satış</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g} className="border-t border-amber-500/10">
                <th className="px-3 py-2 text-left font-medium">{g} gram</th>
                <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatTry(gram24 * g)}</td>
                <td className="px-3 py-2 text-right">{formatTry(gram22 * g)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}